import { Controller } from "@hotwired/stimulus"
import { apiJson, formatApiErrorDetail } from "../lib/api_errore"
import { Disegno, h, link, sincronizza, testo } from "../lib/disegno"

// «1. Richieste da acquisire» nell'elenco di Verifica bandi (components/RichiesteOpportunita.js di 31a5bd1, delta 07
// #61 / B24; verifica/_richieste_opportunita). Solo l'amministratore della piattaforma: le API /api/admin/opportunita/*
// rispondono 403 a chiunque altro.
// - La coda (GET /api/admin/opportunita/richieste) si rilegge ogni 10 s finché la sezione è nella pagina; un errore
//   lo dice e non mostra mai una coda vuota come risultato.
// - Per una richiesta ferma («attenzione», «interrotta»): «Controlla fonte e documenti» (anteprima, nessuna AI), poi
//   la scelta degli atti (1–8) e la conferma esplicita «Ho controllato …» abilitano «Conferma documenti e avvia
//   acquisizione». Un riscontro ufficiale di chiusura sospende l'acquisizione e mostra la prova.
// - «Questa misura ed edizione sono già nel catalogo?» collega la richiesta a un bando scelto esplicitamente.
// - Le richieste con un bando escono dalla coda e rimandano alla scheda.
// - «Riattiva» (morph dell'elenco) non riscrive la sezione: trattieni.
// - Una richiesta alla volta (busy): i pulsanti si spengono prima della chiamata. Titoli, messaggi e nomi degli atti
//   entrano come testo; i link solo per indirizzi http(s).
const CHIUSI = ["closed", "exhausted", "suspended"]
const FERMI = ["attenzione", "interrotta"]
const COLLEGABILI = ["attenzione", "interrotta", "da_revisionare"]

export default class extends Controller {
  static targets = ["sezione", "titolo", "intro"]
  static values = { catalogo: Array, classe: String, intervallo: { type: Number, default: 10000 } }

  connect() {
    this.attivo = true
    Object.assign(this, { rows: [], urls: {}, collegamenti: {}, anteprime: {}, scelte: {}, conferme: {}, errore: "", esito: "", busy: "" })
    this.disegno = new Disegno()
    this.render()
    this.carica().catch(() => this.mostraErrore("Coda delle richieste non disponibile."))
      .finally(() => { if (this.attivo) this.element.removeAttribute("aria-busy") })
    this.programma()
  }

  disconnect() {
    this.attivo = false
    this.ferma()
  }

  // La rilettura ogni 10 s (setInterval dell'originale); data-rilettura finché è attiva.
  programma() {
    this.ferma()
    this.timer = setInterval(() => this.carica().catch(() => this.mostraErrore("Aggiornamento coda non riuscito.")), this.intervalloValue)
    this.element.dataset.rilettura = "attiva"
  }

  ferma() {
    clearInterval(this.timer)
    delete this.element.dataset.rilettura
  }

  intervalloValueChanged() {
    if (this.attivo) this.programma()
  }

  // «Riattiva» riscrive l'elenco con morph: questa sezione no (in React restava montata con righe, URL scritti e
  // anteprime); passa solo il catalogo aggiornato per la scelta «Bando già nel catalogo…».
  trattieni(event) {
    if (event.target !== this.element) return
    event.preventDefault()
    const attributo = "data-richieste-opportunita-catalogo-value"
    const nuovo = event.detail?.newElement?.getAttribute(attributo)
    if (nuovo != null && nuovo !== this.element.getAttribute(attributo)) this.element.setAttribute(attributo, nuovo)
  }

  catalogoValueChanged() {
    if (this.attivo && this.disegno) this.render()
  }

  async carica() {
    const rows = await apiJson("/api/admin/opportunita/richieste")
    if (!this.attivo) return
    this.rows = rows
    this.render()
  }

  mostraErrore(t) {
    if (!this.attivo) return
    this.errore = t
    this.render()
  }

  async run(id, action) {
    if (this.busy) return
    this.busy = id
    this.errore = ""
    this.esito = ""
    this.render()
    try {
      await action()
    } catch (e) {
      this.errore = formatApiErrorDetail(e?.detail) || "Operazione non riuscita."
    } finally {
      this.busy = ""
      if (this.attivo) this.render()
    }
  }

  url(r, azione) {
    return `/api/admin/opportunita/${encodeURIComponent(r.id)}/${azione}`
  }

  pagina(r) {
    return this.urls[r.id] ?? r.fonte ?? ""
  }

  controlla(r) {
    return this.run(r.id, async () => {
      this.anteprime[r.id] = null
      this.scelte[r.id] = []
      this.conferme[r.id] = false
      const data = await apiJson(this.url(r, "anteprima-fonte"), { method: "POST", body: { url: this.pagina(r) } })
      this.anteprime[r.id] = data
    })
  }

  riprendi(r) {
    return this.run(r.id, async () => {
      await apiJson(this.url(r, "fonte"), {
        method: "POST",
        body: { url: this.pagina(r), anteprima_id: this.anteprime[r.id].id, documenti: this.scelte[r.id], stessa_misura_confermata: this.conferme[r.id] === true },
      })
      this.anteprime[r.id] = null
      this.esito = `Acquisizione richiesta per «${testo(r.titolo)}». Segui lo stato qui sotto; il bando resta da validare.`
      await this.carica()
    })
  }

  collega(r) {
    return this.run(r.id, async () => {
      await apiJson(this.url(r, "collega"), { method: "POST", body: { grant_id: this.collegamenti[r.id], versione_confermata: true } })
      this.esito = "Richiesta collegata alla scheda esistente, senza nuova analisi AI."
      await this.carica()
    })
  }

  // ---- disegno ---------------------------------------------------------------------------------------------------

  bottone(t, attrs = {}) {
    return h("button", { type: "button", class: this.classeValue, ...attrs }, t)
  }

  render() {
    if (!this.attivo) return
    const D = this.disegno
    const inAttesa = this.rows.filter((r) => !r.grant_id)
    const acquisite = this.rows.filter((r) => r.grant_id)
    this.titoloTarget.textContent = `1. Richieste da acquisire (${inAttesa.length})`
    const nodi = [this.titoloTarget, this.introTarget]
    if (this.errore) nodi.push(D.nodo("errore", this.errore, () => h("p", { role: "alert", class: "text-amber-800" }, this.errore)))
    if (this.esito) nodi.push(D.nodo("esito", this.esito, () => h("p", { role: "status", class: "text-blue-800" }, this.esito)))
    if (!inAttesa.length && !this.errore) nodi.push(D.nodo("vuota", "", () => h("p", { class: "text-sm" }, "Nessuna richiesta da acquisire.")))
    inAttesa.forEach((r) => nodi.push(this.articolo(r)))
    if (acquisite.length) {
      nodi.push(D.nodo("acquisite", JSON.stringify(acquisite), () => h("details", { class: "border-t pt-3 text-sm", "data-chiave": "acquisite" },
        h("summary", { class: "cursor-pointer" }, `Richieste già acquisite o collegate (${acquisite.length})`),
        h("p", { class: "text-zinc-600" }, "Prosegui dalla scheda del bando nell’elenco di validazione."),
        acquisite.map((r) => h("p", { class: "mt-2" },
          h("a", { class: "underline", href: `/app/verifica/${encodeURIComponent(r.grant_id)}`, "data-turbo-frame": "_top" }, testo(r.titolo)),
          ` — ${testo(r.messaggio)}`)))))
    }
    sincronizza(this.sezioneTarget, nodi)
    D.fine()
  }

  articolo(r) {
    const D = this.disegno
    const k = (x) => `${r.id}:${x}`
    const art = D.nodo(k("articolo"), "", () => h("article", { class: "border-t pt-3 space-y-2" }))
    art.setAttribute("aria-label", testo(r.titolo))
    const evidence = r.riscontro_fonte
    const nonDisponibile = evidence && CHIUSI.includes(evidence.stato)
    const quando = r.aggiornato_il ? new Date(r.aggiornato_il).toLocaleString("it-IT") : "non disponibile"
    const figli = [
      D.nodo(k("titolo"), testo(r.titolo), () => h("div", { class: "font-medium" }, testo(r.titolo))),
      D.nodo(k("stato"), `${r.stato}|${quando}`, () => h("p", { class: "text-xs text-zinc-500" }, `Stato: ${testo(r.stato)} · Aggiornamento: ${quando}`)),
      D.nodo(k("messaggio"), testo(r.messaggio), () => h("p", { class: "text-sm" }, testo(r.messaggio))),
    ]
    if (r.fonte_originaria) {
      figli.push(D.nodo(k("originaria"), r.fonte_originaria, () => link(r.fonte_originaria, { class: "text-xs underline block break-all", target: "_blank", rel: "noreferrer" },
                                                                         `Link ricevuto dalla fonte informativa: ${r.fonte_originaria}`)))
    }
    if (r.fonte) {
      figli.push(D.nodo(k("fonte"), r.fonte, () => link(r.fonte, { class: "text-sm underline block break-all", target: "_blank", rel: "noreferrer" },
                                                       "Apri pagina di acquisizione — corrispondenza con il bando da verificare")))
    }
    if (nonDisponibile) {
      figli.push(D.nodo(k("chiusa"), JSON.stringify(evidence), () => h("div", { class: "text-sm text-amber-900 bg-amber-50 p-3 rounded" },
        h("b", {}, "Sportello non disponibile: acquisizione sospesa."), " ", testo(evidence.nota),
        link(evidence.url, { class: "underline block", target: "_blank", rel: "noreferrer" }, `Riscontro ufficiale del ${testo(evidence.rilevato_il)}`))))
    } else if (FERMI.includes(r.stato)) {
      figli.push(this.ripresa(r))
    }
    if (!nonDisponibile && COLLEGABILI.includes(r.stato)) figli.push(this.collegamento(r))
    sincronizza(art, figli)
    return art
  }

  ripresa(r) {
    const D = this.disegno
    const k = (x) => `${r.id}:${x}`
    const box = D.nodo(k("ripresa"), "", () => {
      const input = h("input", { class: "border rounded px-2 py-2 text-sm w-full", type: "url", on: { input: (e) => {
        this.urls[r.id] = e.target.value
        this.anteprime[r.id] = null
        this.conferme[r.id] = false
        this.render()
      } } })
      const etichetta = h("label", { class: "text-sm block" }, "Pagina ufficiale con gli atti", input)
      const nota = h("p", { class: "text-xs text-zinc-600" },
        "Il dominio ammesso non certifica il bando. Se la pagina rimanda a un altro portale per i documenti, indica la pagina di quel portale. ",
        h("a", { class: "underline", href: "/app/censimento", "data-turbo-frame": "_top" }, "Gestisci fonti e domini ammessi"), ".")
      const controlla = this.bottone("", { on: { click: () => { const riga = this.rows.find((x) => x.id === r.id); if (riga) this.controlla(riga) } } })
      const avviso = h("span", { class: "text-xs text-zinc-500 ml-2" }, "Nessuna analisi AI in questo controllo.")
      const manuale = h("details", { class: "text-xs", "data-chiave": "manuale" },
        h("summary", { class: "cursor-pointer" }, "Hai già gli atti da caricare manualmente?"),
        h("p", {}, "Crea una scheda da «Nuovo bando» nel ", h("a", { class: "underline", href: "/app/bandi", "data-turbo-frame": "_top" }, "Catalogo bandi"),
          ", poi collegala qui usando «Questa misura ed edizione sono già nel catalogo?»."))
      const div = h("div", { class: "space-y-2" })
      Object.assign(div, { input, fissi: [etichetta, nota, controlla, avviso, manuale], controlla })
      return div
    })
    box.input.setAttribute("aria-label", `Pagina ufficiale ${testo(r.titolo)}`)
    if (box.input.value !== this.pagina(r)) box.input.value = this.pagina(r)
    box.controlla.disabled = !!this.busy || !this.pagina(r).trim()
    box.controlla.textContent = this.busy === r.id ? "Operazione in corso…" : "Controlla fonte e documenti"
    const preview = this.anteprime[r.id]
    sincronizza(box, [...box.fissi, preview ? this.documenti(r, preview) : null])
    return box
  }

  documenti(r, preview) {
    const fs = this.disegno.nodo(`${r.id}:anteprima`, JSON.stringify(preview), () => {
      const caselle = new Map()
      const righe = preview.documenti.map((d) => {
        const input = h("input", { type: "checkbox", "aria-label": `Seleziona ${d.label || d.url}`, on: { change: (e) => {
          const selected = this.scelte[r.id] || []
          this.scelte[r.id] = e.target.checked ? [...selected, d.url] : selected.filter((u) => u !== d.url)
          this.render()
        } } })
        caselle.set(d.url, input)
        return h("label", { class: "flex gap-2 text-sm items-start" }, input,
          h("span", { class: "min-w-0 break-words" }, link(d.url, { target: "_blank", rel: "noreferrer", class: "underline" }, testo(d.label || d.url)),
            d.heading ? h("span", { class: "block text-xs text-zinc-500" }, testo(d.heading)) : null))
      })
      const conferma = h("input", { type: "checkbox", on: { change: (e) => { this.conferme[r.id] = e.target.checked; this.render() } } })
      const avvia = this.bottone("Conferma documenti e avvia acquisizione", { on: { click: () => { const riga = this.rows.find((x) => x.id === r.id); if (riga) this.riprendi(riga) } } })
      const el = h("fieldset", { class: "border rounded p-3 space-y-2" },
        h("legend", { class: "text-sm font-semibold" }, `Documenti individuati (${preview.documenti.length})`),
        h("p", { class: "text-sm" }, testo(preview.messaggio)),
        righe,
        h("label", { class: "flex gap-2 text-sm" }, conferma, "Ho controllato che pagina e atti selezionati appartengono a questa misura ed edizione. Non sto confermando la validazione del bando."),
        h("p", { class: "text-xs text-zinc-600" }, "Seleziona da 1 a 8 atti. La conferma avvia l’acquisizione e può avviare l’analisi AI a consumo; restano i limiti giornalieri e i controlli sulla lettura completa."),
        avvia)
      Object.assign(el, { caselle, conferma, avvia })
      return el
    })
    const selected = this.scelte[r.id] || []
    fs.caselle.forEach((input, url) => { input.checked = selected.includes(url) })
    fs.conferma.checked = this.conferme[r.id] || false
    fs.avvia.disabled = !!this.busy || !this.conferme[r.id] || !selected.length || selected.length > 8
    return fs
  }

  collegamento(r) {
    const el = this.disegno.nodo(`${r.id}:collega`, JSON.stringify(this.catalogoValue), () => {
      const select = h("select", { class: "border rounded text-sm max-w-sm", on: { change: (e) => { this.collegamenti[r.id] = e.target.value; this.render() } } },
        h("option", { value: "" }, "Bando già nel catalogo…"),
        this.catalogoValue.map((g) => h("option", { value: testo(g.id) }, testo(g.name))))
      const bottone = this.bottone("Confermo la stessa versione e collego", { on: { click: () => { const riga = this.rows.find((x) => x.id === r.id); if (riga) this.collega(riga) } } })
      const d = h("details", { class: "text-sm", "data-chiave": "collega" },
        h("summary", { class: "cursor-pointer" }, "Questa misura ed edizione sono già nel catalogo?"),
        h("div", { class: "flex flex-wrap gap-2 pt-2" }, select, bottone))
      Object.assign(d, { select, bottone })
      return d
    })
    el.select.setAttribute("aria-label", `Stessa versione ${testo(r.titolo)}`)
    if (el.select.value !== (this.collegamenti[r.id] || "")) el.select.value = this.collegamenti[r.id] || ""
    el.bottone.disabled = !!this.busy || !this.collegamenti[r.id]
    return el
  }
}
